<template>
  <section :class="$style['large-slide-contents-cms-parts']">
    <div :class="$style.container">
      <div :class="$style.list">
        <ClientOnly>
          <Swiper
            :modules="[SwiperAutoplay, SwiperNavigation]"
            class="swiper-container"
            :speed="1000"
            :slides-per-view="1"
            :loop="true"
            :navigation="{
              prevEl: '.swiper-button-prev',
              nextEl: '.swiper-button-next',
            }"
            :watch-overflow="false"
          >
            <SwiperSlide
              v-for="(slideContent, _index) in largeSlideContentsWithImageSet"
              :key="_index"
              class="swiper-slide"
            >
              <AtomsConsumerLink :url="slideContent.url ?? ''" class="_large-slide-item">
                <div class="_image-box">
                  <AtomsConsumerImage
                    :src="slideContent.pc_img_url ?? ''"
                    :alt="`${slideContent.title} - スライド画像`"
                    :width="1024"
                    :height="383"
                    class="_img _pc"
                  />
                  <AtomsConsumerImage
                    :src="slideContent.sp_img_url ?? ''"
                    :alt="`${slideContent.title} - スライド画像`"
                    :width="375"
                    :height="375"
                    class="_img _sp"
                  />
                </div>
                <div class="_text-box">
                  <h3
                    class="_title page-parts-pc-only"
                    :class="[fontWeight, fontSize]"
                    v-html="sanitizeHtmlContent(slideContent.title)"
                  />
                  <h3
                    class="_title page-parts-tb"
                    :class="[fontWeight]"
                    :style="{ fontSize: '14px' }"
                    v-html="sanitizeHtmlContent(slideContent.title)"
                  ></h3>
                  <h3
                    class="_title page-parts-sp"
                    :class="[fontWeight, fontSize]"
                    v-html="sanitizeHtmlContent(slideContent.title)"
                  />
                  <p
                    class="_sub-caption page-parts-pc-only"
                    :class="[subFontWeight, subFontSize]"
                    v-html="sanitizeHtmlContent(slideContent.description)"
                  ></p>
                  <p
                    class="_sub-caption page-parts-tb"
                    :class="[subFontWeight]"
                    :style="{ fontSize: '20px' }"
                    v-html="sanitizeHtmlContent(slideContent.description)"
                  ></p>
                  <p
                    class="_sub-caption page-parts-sp"
                    :class="[subFontSize, subFontWeight]"
                    v-html="sanitizeHtmlContent(slideContent.description)"
                  ></p>
                  <div class="_link-button-box">
                    <AtomsConsumerLink :url="slideContent.url ?? ''" class="_link-button">{{
                      slideContent.link_text
                    }}</AtomsConsumerLink>
                  </div>
                </div>
              </AtomsConsumerLink>
            </SwiperSlide>

            <div class="swiper-button-prev"></div>
            <div class="swiper-button-next"></div>
          </Swiper>
        </ClientOnly>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { sanitizeHtmlContent } from "@tential/ec-gql-schema/utils/functions/sanitize"
import { PagePartsFragmentFragment } from "~/types/pageParts"

type Props = {
  largeSlideContents: PagePartsFragmentFragment["list"]
  fontSize?: string[]
  subFontSize?: string[]
  fontWeight?: string
  subFontWeight?: string
}

const props = withDefaults(defineProps<Props>(), {
  largeSlideContents: () => [],
  fontSize: () => [],
  subFontSize: () => [],
  fontWeight: "",
  subFontWeight: "",
})

// PC/SP両方の画像が設定されているスライドのみ画面に表示する
const largeSlideContentsWithImageSet = props.largeSlideContents.filter((lsc) => {
  return lsc.pc_img_url && lsc.sp_img_url
})
</script>

<style module lang="scss">
.large-slide-contents-cms-parts {
  overflow: hidden;
  .container {
    display: flex;
    justify-content: center;
    padding: 0 20px;
    @include sp {
      padding: 0;
    }

    .list {
      width: 73.15%;
      min-width: 900px;
      max-width: 1024px;
      margin: 0 auto;
      @include less-tb {
        min-width: 668px;
      }
      @include sp {
        width: 100%;
        min-width: unset;
      }

      // swiper 内のスタイルを書き換える
      :global {
        a._large-slide-item {
          cursor: pointer;
        }

        ._large-slide-item {
          position: relative;
          display: block;
          color: $black;
          text-decoration: none;

          @include sp {
            color: $white;
          }
          ._image-box {
            position: relative;
            overflow: hidden;
            &::before {
              display: block;
              padding-top: 37.45%;
              content: "";

              @include sp {
                padding-top: 100%;
              }
            }
            &::after {
              @include sp {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                content: "";
                background-color: rgba($black, 0.33);
              }
            }
            ._img {
              position: absolute;
              top: 0;
              right: 0;
              bottom: 0;
              left: 0;
              width: 100%;
              height: 100%;
              margin: auto;
              vertical-align: top;
              object-fit: cover;

              &._pc {
                @include sp {
                  display: none;
                }
              }
              &._sp {
                display: none;
                @include sp {
                  display: inline;
                }
              }
            }
          }
          ._text-box {
            position: absolute;
            top: 50%;
            left: 9.3%;
            transform: translateY(-50%);

            @include less-tb {
              left: 7%;
            }

            @include sp {
              top: 67%;
              left: 18%;
            }
            ._title {
              margin-bottom: 1px;
              font-size: 1.09375rem;
              font-weight: 700;
              line-height: 30px;

              @include sp {
                margin-bottom: 5px;
                font-size: $font-size-15;
                line-height: 28px;
              }
            }
            ._sub-caption {
              margin-bottom: 1.125rem;
              font-size: 1.09375rem;
              font-weight: 700;
              line-height: 36.5px;
              @include less-tb {
                line-height: 1.5;
              }
              @include sp {
                margin-bottom: 1.5rem;
                line-height: 34.5px;
                letter-spacing: 0.07rem;
              }
            }
            ._link-button-box {
              ._link-button {
                position: relative;
                display: inline-block;
                padding: 9px 1.25rem;
                font-size: $font-size-13;
                line-height: 18px;
                color: $white;
                text-align: center;
                text-decoration: none;
                letter-spacing: 0.15rem;
                background-color: #6f7179;
                border-radius: 90px;

                @include sp {
                  padding: 9px 1.75rem 9px 1.125rem;
                }

                &:after {
                  @include sp {
                    position: absolute;
                    top: 50%;
                    right: 7%;
                    width: 8px;
                    height: 10px;
                    content: "";
                    background: url("/images/common/arrow/arrow-right-white.svg") no-repeat;
                    background-size: 75%;
                    transform: translateY(-50%);
                  }
                }
              }
            }
          }
        }

        .swiper-button-prev,
        .swiper-button-next {
          color: $white;
          background-image: none;
          transition: opacity 0.2s ease-out 0s;

          &:hover {
            cursor: pointer;
          }

          &::after {
            font-size: $font-size-30;
            font-weight: 700;

            @include sp {
              font-weight: 400;
            }
          }
        }
      }
    }
  }
}
</style>
